import React, { useState, useCallback } from 'react';
import Button, { InlineTextButton, SecondaryButton } from '../Button/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  canShowBuyerConfirmSold,
  canShowMarkAsSold,
  getListingBuyerCompanyName,
  getListingStatus,
  hasBuyingRequestFromCompany,
  isExpired,
  isListingSold,
} from '../../util/listing';
import { LISTING_STATUS, PENDING_INTAKE_STATUS } from '../../constants/listing';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import MarkAsSoldModal from './MarkAsSoldModal/MarkAsSoldModal';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useDispatch, useSelector } from 'react-redux';
import { createSlug } from '../../util/urlHelpers';
import Confirm from '../../modules/confirm';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import api from '../../api';
import RequestToBuyModal from './RequestToBuyModal/RequestToBuyModal';
import { getUserCompanyId } from '../../util/user';
import CustomerContactedModal from './CustomerContactedModal/CustomerContactedModal';
import CustomerDecisionModal from './CustomerDecisionModal/CustomerDecisionModal';

import css from './ContractLeaseActions.module.css';

const ContractLeaseActions = props => {
  const { className, listing, isInline, handleUpdatedListing, isTableView = false } = props;

  const intl = useIntl();
  const history = useHistory();
  const routes = useRouteConfiguration();
  const dispatch = useDispatch();
  const [isSoldModalOpen, setIsSoldModalOpen] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [isRequestToBuyModalOpen, setIsRequestToBuyModalOpen] = useState(false);
  const [isCustomerContactedModalOpen, setIsCustomerContactedModalOpen] = useState(false);
  const [isCustomerDecisionModalOpen, setIsCustomerDecisionModalOpen] = useState(false);

  const { currentUser } = useSelector(state => state.user);

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  const markAsReceivedProduct = async () => {
    try {
      setActionInProgress(true);
      setActionError(null);

      await api.listings.markAsReceived(listing.id.uuid);
    } catch (error) {
      setActionError(error);
    } finally {
      setTimeout(() => window.location.reload(), 100);
      setActionInProgress(false);
    }
  };

  const archiveListing = async () => {
    const hasApprovedAction = await Confirm({
      title: intl.formatMessage({ id: 'ContractLeaseActions.modalArchiveTitle' }),
      description: intl.formatMessage({ id: 'ContractLeaseActions.modalArchiveDesc' }),
      confirmationText: intl.formatMessage({ id: 'General.confirm' }),
      cancelText: intl.formatMessage({ id: 'General.cancel' }),
      onManageDisableScrolling,
    });

    if (!hasApprovedAction) return;

    try {
      setActionInProgress(true);
      setActionError(null);

      await api.listings.changeListingStatus(listing.id.uuid, 'archived');

      setTimeout(() => window.location.reload(), 100);
    } catch (error) {
      setActionError(error);
    } finally {
      setActionInProgress(false);
    }
  };

  if (!listing || !currentUser) return null;

  const status = getListingStatus(listing);

  const classes = classNames(css.root, { [css.inline]: isInline }, className);

  const ButtonComponent = isInline ? InlineTextButton : Button;

  let actions = null;

  const handleAcceptDeclineSold = async isAccept => {
    const hasApprovedAction = await Confirm({
      title: isAccept
        ? intl.formatMessage({ id: 'ContractLeaseActions.modalAcceptSoldTitle' })
        : intl.formatMessage({ id: 'ContractLeaseActions.modalDeclineSoldTitle' }),
      description: isAccept
        ? intl.formatMessage(
            { id: 'ContractLeaseActions.modalAcceptSoldDesc' },
            { transferToName: getListingBuyerCompanyName(listing) || '-' }
          )
        : null,
      confirmationText: intl.formatMessage({ id: 'General.confirm' }),
      cancelText: intl.formatMessage({ id: 'General.cancel' }),
      onManageDisableScrolling,
    });

    if (!hasApprovedAction) return;

    try {
      setActionInProgress(true);
      setActionError(null);

      const apiFn = isAccept ? api.listings.confirmListingSold : api.listings.declineListingSold;

      const { data: resListing } = await apiFn(listing.id.uuid);

      handleUpdatedListing?.(resListing);

      listing.attributes.metadata = resListing.attributes.metadata;
    } catch (error) {
      setActionError(error);
    } finally {
      setActionInProgress(false);
    }
  };

  const handlePrintProductSheet = async () => {
    try {
      setActionInProgress(true);
      setActionError(null);

      const { data } = await api.listings.printProductSheet(listing.id.uuid);

      const blob = new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `product-sheet-${listing.id.uuid}.pdf`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      setActionError(error);
    } finally {
      setActionInProgress(false);
    }
  };

  const { metadata = {} } = listing?.attributes || {};
  const { hasContactedLeasingUser, leaseExpirationOption } = metadata;

  // In case I’m previewing a listing that has the clients decision “New leasing offering” or “Finalised contract” and the contra
  const canShowMarkAsReceived =
    isExpired(listing) &&
    (leaseExpirationOption === PENDING_INTAKE_STATUS.NEW_LEASING_OFFERING ||
      leaseExpirationOption === PENDING_INTAKE_STATUS.NOTHING);

  // Applicable only in case the listing has a client decision “Prolong leasing” or “Customer buyout” and the contract end date is passed
  const canShowArchive =
    isExpired(listing) &&
    (leaseExpirationOption === PENDING_INTAKE_STATUS.PROLONG_LEASING ||
      leaseExpirationOption === PENDING_INTAKE_STATUS.CUSTOMER_BUY_OUT);

  switch (status) {
    case LISTING_STATUS.PENDING_INTAKE: {
      actions = (
        <div className={css.tableActionsWrapper}>
          {isTableView && !hasContactedLeasingUser && (
            <>
              <ButtonComponent
                type="button"
                onClick={() => setIsCustomerContactedModalOpen(true)}
                disabled={actionInProgress}
              >
                {intl.formatMessage({ id: 'TableView.customerContacted' })}
              </ButtonComponent>

              {isCustomerContactedModalOpen && (
                <CustomerContactedModal
                  isOpen={isCustomerContactedModalOpen}
                  onClose={() => setIsCustomerContactedModalOpen(false)}
                  listing={listing}
                />
              )}
            </>
          )}

          {isTableView &&
            hasContactedLeasingUser &&
            (!leaseExpirationOption || leaseExpirationOption === 'not-set') && (
              <>
                <ButtonComponent
                  type="button"
                  onClick={() => setIsCustomerDecisionModalOpen(true)}
                  disabled={actionInProgress}
                >
                  {intl.formatMessage({ id: 'TableView.customerDecision' })}
                </ButtonComponent>

                {isCustomerDecisionModalOpen && (
                  <CustomerDecisionModal
                    isOpen={isCustomerDecisionModalOpen}
                    onClose={() => setIsCustomerDecisionModalOpen(false)}
                    listing={listing}
                  />
                )}
              </>
            )}

          {canShowMarkAsReceived && (
            <ButtonComponent
              type="button"
              onClick={() => markAsReceivedProduct()}
              disabled={actionInProgress}
            >
              <FormattedMessage id="ContractLeaseActions.markAsReceived" />
            </ButtonComponent>
          )}

          {canShowArchive && (
            <ButtonComponent
              type="button"
              onClick={archiveListing}
              disabled={actionInProgress}
              className={css.declineNormal}
            >
              <FormattedMessage id="ContractLeaseActions.archive" />
            </ButtonComponent>
          )}
        </div>
      );

      break;
    }
    case LISTING_STATUS.PENDING_REVIEW:
      actions = (
        <ButtonComponent
          type="button"
          onClick={() => {
            history.push(
              createResourceLocatorString('EditListingPage', routes, {
                slug: createSlug(listing.attributes.title),
                type: 'edit',
                id: listing.id.uuid,
                tab: 'contract-details',
              })
            );
          }}
        >
          {intl.formatMessage({ id: 'General.startReview' })}
        </ButtonComponent>
      );
      break;

    case LISTING_STATUS.SET_FOR_REFURBISHMENT:
      actions = (
        <ButtonComponent
          type="button"
          onClick={() => {
            history.push(
              createResourceLocatorString('EditListingPage', routes, {
                slug: createSlug(listing.attributes.title),
                type: 'edit',
                id: listing.id.uuid,
                tab: 'contract-details',
              })
            );
          }}
        >
          {intl.formatMessage({ id: 'General.finishRefurbishing' })}
        </ButtonComponent>
      );
      break;

    case LISTING_STATUS.REFURBISHED:
      actions = (
        <ButtonComponent
          type="button"
          onClick={() => {
            history.push(
              createResourceLocatorString('EditListingPage', routes, {
                slug: createSlug(listing.attributes.title),
                type: 'edit',
                id: listing.id.uuid,
                tab: 'contract-details',
              })
            );
          }}
        >
          {intl.formatMessage({ id: 'General.offerForSale' })}
        </ButtonComponent>
      );
      break;

    case LISTING_STATUS.PUBLISHED:
      actions = canShowMarkAsSold(currentUser, listing) ? (
        <>
          <ButtonComponent type="button" onClick={() => setIsSoldModalOpen(true)}>
            {intl.formatMessage({ id: 'ContractLeaseActions.markAsSold' })}
          </ButtonComponent>
          {/* render conditionally because we have a useEffect that fetches companies */}
          {isSoldModalOpen && (
            <MarkAsSoldModal
              isOpen={isSoldModalOpen}
              onClose={() => setIsSoldModalOpen(false)}
              listing={listing}
              isSellAsIs={status === LISTING_STATUS.SELL_AS_IS}
            />
          )}
          <SecondaryButton type="button" onClick={handlePrintProductSheet}>
            {intl.formatMessage({ id: 'ContractLeaseActions.printProductSheet' })}
          </SecondaryButton>
        </>
      ) : (
        currentUser &&
        !canShowMarkAsSold(currentUser, listing) && (
          <>
            <ButtonComponent
              type="button"
              onClick={() => setIsRequestToBuyModalOpen(true)}
              disabled={hasBuyingRequestFromCompany(listing, getUserCompanyId(currentUser))}
            >
              {intl.formatMessage({ id: 'ContractLeaseActions.requestToBuy' })}
            </ButtonComponent>
            {isRequestToBuyModalOpen && (
              <RequestToBuyModal
                isOpen={isRequestToBuyModalOpen}
                onClose={() => setIsRequestToBuyModalOpen(false)}
                listingId={listing.id.uuid}
              />
            )}
            <SecondaryButton type="button" onClick={handlePrintProductSheet}>
              {intl.formatMessage({ id: 'ContractLeaseActions.printProductSheet' })}
            </SecondaryButton>
          </>
        )
      );

      break;
    case LISTING_STATUS.SELL_AS_IS:
      actions = canShowMarkAsSold(currentUser, listing) ? (
        <>
          <ButtonComponent type="button" onClick={() => setIsSoldModalOpen(true)}>
            {intl.formatMessage({ id: 'ContractLeaseActions.markAsSold' })}
          </ButtonComponent>
          {/* render conditionally because we have a useEffect that fetches companies */}
          {isSoldModalOpen && (
            <MarkAsSoldModal
              isOpen={isSoldModalOpen}
              onClose={() => setIsSoldModalOpen(false)}
              listing={listing}
              isSellAsIs={status === LISTING_STATUS.SELL_AS_IS}
            />
          )}
        </>
      ) : (
        currentUser &&
        !canShowMarkAsSold(currentUser, listing) && (
          <>
            <ButtonComponent
              type="button"
              onClick={() => setIsRequestToBuyModalOpen(true)}
              disabled={hasBuyingRequestFromCompany(listing, getUserCompanyId(currentUser))}
            >
              {intl.formatMessage({ id: 'ContractLeaseActions.requestToBuy' })}
            </ButtonComponent>
            {isRequestToBuyModalOpen && (
              <RequestToBuyModal
                isOpen={isRequestToBuyModalOpen}
                onClose={() => setIsRequestToBuyModalOpen(false)}
                listingId={listing.id.uuid}
              />
            )}
          </>
        )
      );

      break;

    case LISTING_STATUS.SOLD: {
      const { metadata } = listing?.attributes || {};
      const { shipped } = metadata || {};

      const canShowMarkAsReceived = isListingSold(listing) && shipped === true;

      actions = canShowMarkAsReceived ? (
        <ButtonComponent
          type="button"
          onClick={() => markAsReceivedProduct()}
          disabled={actionInProgress}
        >
          <FormattedMessage id="ContractLeaseActions.markAsReceived" />
        </ButtonComponent>
      ) : canShowBuyerConfirmSold(currentUser, listing) ? (
        <>
          <ButtonComponent
            type="button"
            disabled={actionInProgress}
            onClick={() => handleAcceptDeclineSold(true)}
          >
            {intl.formatMessage({ id: 'General.confirm' })}
          </ButtonComponent>

          <ButtonComponent
            type="button"
            disabled={actionInProgress}
            onClick={() => handleAcceptDeclineSold()}
            className={classNames({
              [css.declineInline]: isInline,
              [css.declineNormal]: !isInline,
            })}
          >
            {intl.formatMessage({ id: 'General.decline' })}
          </ButtonComponent>
        </>
      ) : null;

      break;
    }

    default:
      break;
  }

  return (
    <div className={classes}>
      {actions ? <div className={css.actions}>{actions}</div> : null}
      {actionError && <p className={css.error}>{intl.formatMessage({ id: 'General.error' })}</p>}
    </div>
  );
};

export default ContractLeaseActions;
